import { useCallback, useMemo } from 'react'

import type { UseMutateAsyncFunction } from '@tanstack/react-query'
import { t } from 'i18next'
import { useFormContext, useFieldArray } from 'react-hook-form'

import { obterIdPorTipo, toClienteDto } from '@/features/proposta/entities/Comprador/dto/mapper-comprador-pf-api'
import { toContaDto } from '@/features/proposta/entities/Comprador/dto/mapper-comprador-pj-api'
import { CompradorListagemCommandFactory } from '@/features/proposta/entities/Comprador/form-to-listagem/comprador-listagem-command-factory'
import { obterTodosCompradoresPFEPJ } from '@/features/proposta/entities/Comprador/merge-comprador-pj-pf'
import { obterOpcoesCadastroComprador } from '@/features/proposta/entities/Comprador/opcoes-comprador-menu'
import { ApiCommandFactory } from '@/features/proposta/entities/Comprador/sincronizar-comprador-command'
import { transformarClienteParaEmailDocumento } from '@/features/proposta/entities/Comprador/transformar-emails-clientes'
import { useMutationAtualizarPessoaJuridica } from '@/features/proposta/hooks/use-atualizar-pj'
import { useMutationCadastrarPessoaJuridica } from '@/features/proposta/hooks/use-criar-pj'
import { useExcluirComprador } from '@/features/proposta/hooks/use-excluir-comprador'
import { useOrquestrarCriacaoEdicao } from '@/features/proposta/hooks/use-orquestrar-criacao-edicao'
import type { TPropostaSchemaInput } from '@/features/proposta/schemas'
import type {
  CompradorPessoaFisicaStepSchemaOutput,
  CompradorVinculoStepSchemaOutput,
} from '@/features/proposta/schemas/compradores/comprador-pf.schema'
import type { CompradorPessoaJuridicaSchemaOutput } from '@/features/proposta/schemas/compradores/comprador-pj.schema'
import type { AxiosErrorMap } from '@/infra/http'
import { showToast } from '@/infra/sonner'
import type {
  TCreateCadastrarClienteData,
  TCreateCadastrarClienteResponse,
  TNacionalidade,
  TProfissao,
} from '@/types/cliente'

type TClienteMutations = {
  cadastrarCliente: UseMutateAsyncFunction<
    TCreateCadastrarClienteResponse,
    AxiosErrorMap,
    Partial<TCreateCadastrarClienteData>
  >
  atualizarCliente: UseMutateAsyncFunction<unknown, AxiosErrorMap, TCreateCadastrarClienteData & { id: string }>
}

type TEtapaCompradoresListaDeps = {
  nacionalidades: TNacionalidade[]
  profissoes: TProfissao[]
  clienteMutations: TClienteMutations
}

export function useEtapaCompradoresLista({
  nacionalidades: _nacionalidades,
  profissoes: _profissoes,
  clienteMutations,
}: TEtapaCompradoresListaDeps) {
  const { control, getValues, watch } = useFormContext<TPropostaSchemaInput>()

  const clienteAtendimento = watch('informacoesIniciais.clienteAtendimento')

  const obterClientesAtualizados = useCallback(() => getValues('cadastroCompradores.clientes'), [getValues])
  const obterCompradoresAtualizados = useCallback(() => getValues('cadastroCompradores.compradores'), [getValues])
  const obterProcuradoresAtualizados = useCallback(() => getValues('cadastroCompradores.procuradores'), [getValues])
  const obterClientesJuridicosAtualizados = useCallback(
    () => getValues('cadastroCompradores.clientesJuridicos'),
    [getValues],
  )

  const {
    fields: clientes,
    append: appendCliente,
    update: updateCliente,
    remove: removeCliente,
    replace: replaceClientes,
  } = useFieldArray({ name: 'cadastroCompradores.clientes', control, keyName: 'idField' })

  const {
    fields: compradores,
    append: appendComprador,
    update: updateComprador,
    remove: removeComprador,
    replace: replaceCompradores,
  } = useFieldArray({ name: 'cadastroCompradores.compradores', control, keyName: 'idField' })

  const {
    fields: procuradores,
    append: appendProcurador,
    update: updateProcurador,
    remove: removeProcurador,
    replace: replaceProcuradores,
  } = useFieldArray({ name: 'cadastroCompradores.procuradores', control, keyName: 'idField' })

  const {
    fields: clientesJuridicos,
    append: appendClienteJuridico,
    update: updateClienteJuridico,
    remove: removeClienteJuridico,
    replace: replaceClientesJuridicos,
  } = useFieldArray({ name: 'cadastroCompradores.clientesJuridicos', control, keyName: 'idField' })

  const replaceAll = useCallback(
    (
      cadastro: Pick<
        TPropostaSchemaInput['cadastroCompradores'],
        'clientes' | 'clientesJuridicos' | 'compradores' | 'procuradores'
      >,
    ) => {
      replaceClientes(cadastro.clientes)
      replaceCompradores(cadastro.compradores)
      replaceProcuradores(cadastro.procuradores)
      replaceClientesJuridicos(cadastro.clientesJuridicos)
    },
    [replaceClientes, replaceCompradores, replaceProcuradores, replaceClientesJuridicos],
  )

  const emailsJaVinculados = transformarClienteParaEmailDocumento(clientes)
  const compradoresComum = obterTodosCompradoresPFEPJ(compradores, clientes, clientesJuridicos)
  const isEmpty = compradoresComum.length === 0

  const { handleOnExcluirComprador, obterProcuradoresDoComprador } = useExcluirComprador(
    { clientes, compradores, procuradores, clientesJuridicos },
    { removeCliente, removeComprador, removeProcurador, removeClienteJuridico, updateComprador, updateProcurador },
  )

  const { clienteToEdit, flowKey, handleCloseStepsComprador, handleEdit, mode, openCreate } =
    useOrquestrarCriacaoEdicao(compradores, clientes, clientesJuridicos)

  const { mutateAsync: cadastrarPessoaJuridica } = useMutationCadastrarPessoaJuridica()
  const { mutateAsync: atualizarPessoaJuridica } = useMutationAtualizarPessoaJuridica()

  const handleErrorApi = useCallback(
    (error?: AxiosErrorMap) => {
      if (error?.response?.data.some((err) => err.code === 'DOCUMENT_ALREADY_EXISTS')) {
        showToast({ message: t('proposta:cadastro_compradores.erro.ja_cadastrado'), variant: 'error' })
        return
      }

      if (error?.response?.data.some((err) => err.code === 'INACTIVE_CLIENT')) {
        showToast({ message: t('proposta:cadastro_compradores.erro.cliente_inativo'), variant: 'error' })
        handleCloseStepsComprador()
        return
      }

      showToast({ message: t('errors_gerais.padrao', { ns: 'common' }), variant: 'error' })
    },
    [handleCloseStepsComprador],
  )

  const handleSubmit = useCallback(
    async (
      comprador:
        | CompradorPessoaFisicaStepSchemaOutput
        | CompradorPessoaJuridicaSchemaOutput
        | CompradorVinculoStepSchemaOutput,
      isDirty: boolean,
    ) => {
      const deps = {
        adicionarCliente: appendCliente,
        atualizarCliente: updateCliente,
        adicionarClienteJuridico: appendClienteJuridico,
        atualizarClienteJuridico: updateClienteJuridico,
        adicionarComprador: appendComprador,
        atualizarComprador: updateComprador,
        adicionarProcurador: appendProcurador,
        atualizarProcurador: updateProcurador,
        removeCliente: removeCliente,
      }

      const apiCmd = ApiCommandFactory.criar(comprador, isDirty, {
        fisico: {
          create: (formValue, papel) => {
            const dto = toClienteDto(formValue, papel)
            return clienteMutations.cadastrarCliente(dto, { onError: handleErrorApi })
          },
          update: (formValue, papel) => {
            const dto = toClienteDto(formValue, papel)
            const id = obterIdPorTipo(papel, formValue)
            return clienteMutations.atualizarCliente({ id, ...dto }, { onError: handleErrorApi })
          },
        },
        juridico: {
          create: (formValue) => {
            const dto = toContaDto(formValue)
            return cadastrarPessoaJuridica(dto)
          },
          update: (formValue) => {
            const dto = toContaDto(formValue)
            return atualizarPessoaJuridica({ ...dto, id: formValue.dados.id! })
          },
        },
      })

      const compradorSalvo = await apiCmd.executar()
      const compradoresParaAdicionarVinculo =
        comprador.tipo === 'Procurador' ||
        comprador.tipo === 'Representante' ||
        (comprador.tipo === 'PessoaFisica' && comprador.fluxoIndependente === 'MaePai')

      const isEditing = comprador.mode === 'edit' && clienteToEdit

      const idToUpdate = isEditing ? clienteToEdit.id : null
      const listaCmd =
        isEditing || compradoresParaAdicionarVinculo
          ? CompradorListagemCommandFactory.atualizar(
              compradorSalvo,
              deps,
              obterCompradoresAtualizados,
              obterClientesAtualizados,
              obterClientesJuridicosAtualizados,
              obterProcuradoresAtualizados,
              idToUpdate,
            )
          : CompradorListagemCommandFactory.adicionar(
              compradorSalvo,
              deps,
              obterClientesAtualizados,
              obterCompradoresAtualizados,
              obterProcuradoresAtualizados,
            )
      listaCmd.executar()
      handleCloseStepsComprador()
    },
    [
      appendCliente,
      updateCliente,
      appendClienteJuridico,
      updateClienteJuridico,
      appendComprador,
      updateComprador,
      appendProcurador,
      updateProcurador,
      removeCliente,
      clienteMutations,
      cadastrarPessoaJuridica,
      atualizarPessoaJuridica,
      handleErrorApi,
      clienteToEdit,
      obterCompradoresAtualizados,
      obterClientesAtualizados,
      obterClientesJuridicosAtualizados,
      obterProcuradoresAtualizados,
      handleCloseStepsComprador,
    ],
  )

  const opcoesCadastro = useMemo(() => obterOpcoesCadastroComprador(compradoresComum), [compradoresComum])

  return {
    clientes,
    compradores,
    procuradores,
    clientesJuridicos,
    compradoresComum,
    emailsJaVinculados,
    isEmpty,
    opcoesCadastro,
    clienteAtendimento,
    flowKey,
    mode,
    clienteToEdit,
    openCreate,
    handleEdit,
    handleCloseStepsComprador,
    handleSubmit,
    handleOnExcluirComprador,
    obterProcuradoresDoComprador,
    replaceAll,
  }
}
